import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Activity, Clock, AlertTriangle, Send } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface DailySymptomTrackerProps {
  aiScore: number;
}

export const DailySymptomTracker = ({ aiScore }: DailySymptomTrackerProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [selectedIntensity, setSelectedIntensity] = useState<number | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [selectedTiming, setSelectedTiming] = useState<string>('');
  const [hasNewSymptoms, setHasNewSymptoms] = useState<boolean | null>(null);
  const [functionalCapacity, setFunctionalCapacity] = useState<number>(75);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNewSymptomsDialog, setShowNewSymptomsDialog] = useState(false);

  const handleNewSymptomsSelection = (value: boolean) => {
    setHasNewSymptoms(value);
    if (value) {
      setShowNewSymptomsDialog(true);
    }
  };

  const handleSubmit = async () => {
    if (selectedIntensity === null) {
      toast({
        title: "Missing Information",
        description: "Please select your symptom intensity level.",
        variant: "destructive"
      });
      return;
    }

    if (!user?.id) {
      toast({
        title: "Authentication Required",
        description: "You must be logged in to track symptoms.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const symptomDetails = {
        location: selectedLocation,
        timing: selectedTiming,
        intensity: selectedIntensity,
        functional_capacity: functionalCapacity
      };

      const { error } = await supabase
        .from('daily_symptom_tracker')
        .insert({
          user_id: user.id,
          session_id: `daily_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          symptom_intensity: selectedIntensity,
          symptom_details: symptomDetails,
          has_new_symptoms: hasNewSymptoms,
          functional_capacity: functionalCapacity,
          notes: `Location: ${selectedLocation}, Timing: ${selectedTiming}`
        });

      if (error) {
        throw error;
      }

      toast({
        title: "Symptoms Tracked",
        description: "Your daily symptom assessment has been saved successfully.",
      });

      // Reset form
      setSelectedIntensity(null);
      setSelectedLocation('');
      setSelectedTiming('');
      setHasNewSymptoms(null);
      setFunctionalCapacity(75);

    } catch (error) {
      console.error('Error saving symptom tracking:', error);
      toast({
        title: "Save Failed",
        description: "Unable to save your symptom tracking. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormComplete = selectedIntensity !== null && selectedLocation && selectedTiming && hasNewSymptoms !== null;

  return (
    <div className="space-y-4">
      {/* Tracking Schedule Alert */}
      {aiScore >= 60 && (
        <div className="p-3 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg animate-fade-in">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-green-600 animate-pulse" />
            <span className="text-sm font-medium text-green-800">3x daily tracking required • Morning, afternoon, evening assessments</span>
            <Badge variant="secondary" className="text-xs">Critical Monitoring</Badge>
          </div>
        </div>
      )}

      {/* Symptom Intensity Scale */}
      <Card className="group border-blue-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" />
            Symptom Intensity Scale
            <Badge variant="outline" className="text-xs text-blue-600">
              Check {aiScore >= 60 ? "3x daily" : aiScore >= 40 ? "2x daily" : "daily"}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="w-full max-w-full overflow-hidden border border-border/40 rounded-lg bg-background">
              {/* Clinical Header */}
              <div className="px-4 py-3 border-b border-border/30 bg-muted/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    <span className="text-sm font-semibold text-foreground">Pain Assessment Scale</span>
                    <Badge variant="outline" className="text-xs">0-10 NRS</Badge>
                  </div>
                  <span className="text-xs text-muted-foreground">Select current level</span>
                </div>
              </div>
              
              {/* Scale Grid */}
              <div className="p-4">
                <div className="w-full overflow-x-auto pb-2">
                  <div className="flex gap-1 min-w-full justify-between">
                    {[0,1,2,3,4,5,6,7,8,9,10].map(i => (
                      <button 
                        key={i}
                        onClick={() => setSelectedIntensity(i)}
                        className={`
                          flex-1 min-w-[32px] max-w-[40px] h-10 
                          rounded-md text-sm font-semibold 
                          border transition-all duration-200 ease-in-out
                          ${selectedIntensity === i 
                            ? 'ring-2 ring-primary/20 shadow-sm scale-105' 
                            : 'hover:scale-105'
                          } ${
                            i <= 3 ? `${selectedIntensity === i ? 'bg-green-500 text-white border-green-600' : 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200'}` :
                            i <= 6 ? `${selectedIntensity === i ? 'bg-yellow-500 text-white border-yellow-600' : 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200'}` :
                            `${selectedIntensity === i ? 'bg-red-500 text-white border-red-600' : 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200'}`
                          }
                        `}
                      >
                        {i}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Clinical Scale Labels */}
                <div className="flex justify-between text-xs text-muted-foreground mt-3 px-1">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500/60 rounded-full"></span>
                    No pain
                  </span>
                  <span>Mild</span>
                  <span>Moderate</span>
                  <span>Severe</span>
                  <span className="flex items-center gap-1">
                    Critical
                    <span className="w-2 h-2 bg-red-500/60 rounded-full"></span>
                  </span>
                </div>
                
                {/* Selected Value Display */}
                {selectedIntensity !== null && (
                  <div className="mt-4 p-3 bg-muted/30 rounded-lg border border-border/40">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">
                        Current Assessment: {selectedIntensity}/10
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        {selectedIntensity <= 3 ? 'Mild' :
                         selectedIntensity <= 6 ? 'Moderate' :
                         'Severe'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {selectedIntensity <= 3 ? 'Minor discomfort - manageable pain levels' :
                       selectedIntensity <= 6 ? 'Noticeable symptoms - may interfere with activities' :
                       'Significant pain - consider immediate medical consultation'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Symptom Details */}
      <Card className="group border-purple-100 hover:border-purple-200 hover:shadow-lg transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-purple-700">
            Symptom Details
            <Badge variant="outline" className="text-xs text-purple-600">Pattern Analysis</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-purple-800">Primary Location:</h4>
              <div className="grid grid-cols-2 gap-2">
                {['Head', 'Chest', 'Back', 'Limbs'].map(location => (
                  <button 
                    key={location}
                    onClick={() => setSelectedLocation(location)}
                    className={`text-sm p-2 border rounded hover:bg-purple-100 transition-colors ${
                      selectedLocation === location 
                        ? 'bg-purple-200 border-purple-400 text-purple-800' 
                        : 'bg-white border-purple-200'
                    }`}
                  >
                    {location}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-purple-800">Timing Pattern:</h4>
              <div className="grid grid-cols-2 gap-2">
                {['Morning', 'Afternoon', 'Evening', 'Night'].map(time => (
                  <button 
                    key={time}
                    onClick={() => setSelectedTiming(time)}
                    className={`text-sm p-2 border rounded hover:bg-purple-100 transition-colors ${
                      selectedTiming === time 
                        ? 'bg-purple-200 border-purple-400 text-purple-800' 
                        : 'bg-white border-purple-200'
                    }`}
                  >
                    {time}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* New Symptoms Alert */}
          <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-orange-800">Any new symptoms today?</span>
              <div className="flex gap-2">
                <button 
                  onClick={() => handleNewSymptomsSelection(false)}
                  className={`text-sm px-3 py-1 rounded transition-colors ${
                    hasNewSymptoms === false 
                      ? 'bg-green-200 text-green-800' 
                      : 'bg-green-100 text-green-800 hover:bg-green-200'
                  }`}
                >
                  No
                </button>
                <button 
                  onClick={() => handleNewSymptomsSelection(true)}
                  className={`text-sm px-3 py-1 rounded transition-colors ${
                    hasNewSymptoms === true 
                      ? 'bg-red-200 text-red-800' 
                      : 'bg-red-100 text-red-800 hover:bg-red-200'
                  }`}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Submit Button */}
      <div className="flex justify-center">
        <Button
          onClick={handleSubmit}
          disabled={!isFormComplete || isSubmitting}
          className="flex items-center gap-2 px-6 py-2"
        >
          <Send className="h-4 w-4" />
          {isSubmitting ? 'Saving...' : 'Submit Daily Tracking'}
        </Button>
      </div>

      {/* New Symptoms Dialog */}
      <Dialog open={showNewSymptomsDialog} onOpenChange={setShowNewSymptomsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              New Symptoms Detected
            </DialogTitle>
            <DialogDescription className="space-y-3">
              <p>You've indicated that you have new symptoms today. This is important information for your health monitoring.</p>
              <div className="p-3 bg-orange-50 border border-orange-200 rounded">
                <p className="text-sm font-medium text-orange-800 mb-2">Recommended Action:</p>
                <p className="text-sm text-orange-700">
                  Consider taking another full health assessment to properly evaluate your new symptoms and get updated recommendations.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowNewSymptomsDialog(false)}
              className="flex-1"
            >
              Continue Tracking
            </Button>
            <Button
              onClick={() => {
                setShowNewSymptomsDialog(false);
                window.location.href = '/';
              }}
              className="flex-1"
            >
              Take New Assessment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};